/**
 * The small pieces every document screen repeats: the status chip, the tab, the
 * empty state, the count badge.
 *
 * They live here rather than in each screen because they carry MEANING, not
 * just styling — a filled chip is a lifecycle state, an outline chip is a fact
 * about the document that is not its state, and confusing the two is how a
 * reader ends up acting on a superseded revision. One implementation is the
 * only way that distinction survives the next edit.
 */

import type { CSSProperties, ReactNode } from 'react'
import {
  RADIUS, T, TYPE, toneColor, toneWash, btnStyle, type Tone,
} from './styles'

/**
 * A document's LIFECYCLE state — filled, with a dot.
 *
 * Reserved for the state machine (draft / waiting for approval / approved /
 * in force / withdrawn). Anything else uses `FactChip`.
 */
export function StatusChip({ tone = 'neutral', children, title }: {
  tone?: Tone
  children: ReactNode
  title?: string
}) {
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: RADIUS.full,
      background: toneWash(tone), color: toneColor(tone),
      fontSize: TYPE.small, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: RADIUS.full,
        background: toneColor(tone), flexShrink: 0,
      }} />
      {children}
    </span>
  )
}

/**
 * A fact about a document that is NOT its lifecycle state — "read on screen
 * only", "someone is writing the next version", "please confirm you have read
 * it". Outlined so it can never be mistaken for the state chip beside it.
 */
export function FactChip({ tone = 'neutral', children, title }: {
  tone?: Tone
  children: ReactNode
  title?: string
}) {
  const color = tone === 'neutral' ? T.textSecondary : toneColor(tone)
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: RADIUS.full,
      border: `1px solid ${tone === 'neutral' ? T.border : color}`,
      color, fontSize: TYPE.small, fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  )
}

/** A count beside a tab label. Absent at zero — a zero is not news. */
export function CountBadge({ value, tone = 'accent' }: { value: number; tone?: Tone }) {
  if (!value) return null
  return (
    <span style={{
      minWidth: 18, padding: '0 6px', borderRadius: RADIUS.full,
      background: toneColor(tone), color: '#ffffff',
      fontSize: 13, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
    }}>
      {value}
    </span>
  )
}

export function TabButton({ active, onClick, children }: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...btnStyle,
        background: active ? T.accentWeak : 'transparent',
        borderColor: active ? T.accent : 'transparent',
        color: active ? T.accent : T.textSecondary,
        fontWeight: active ? 600 : 500,
        fontSize: TYPE.meta,
      }}
    >
      {children}
    </button>
  )
}

/**
 * Nothing to show. Says what would be here and what to do about it — an empty
 * panel that only says "none" leaves the reader wondering whether it is broken.
 */
export function EmptyState({ headline, hint, action }: {
  headline: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 8, padding: '48px 24px', textAlign: 'center',
    }}>
      <div style={{ fontSize: TYPE.heading, fontWeight: 600, color: T.text }}>
        {headline}
      </div>
      {hint ? (
        <div style={{ fontSize: TYPE.meta, color: T.textSecondary, maxWidth: 420, lineHeight: 1.55 }}>
          {hint}
        </div>
      ) : null}
      {action ? <div style={{ marginTop: 4 }}>{action}</div> : null}
    </div>
  )
}

/** A screen title with the sentence that says what the screen is for. */
export function PageHeading({ title, blurb, actions, style }: {
  title: string
  blurb?: string
  actions?: ReactNode
  style?: CSSProperties
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, ...style }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: TYPE.title, fontWeight: 600, letterSpacing: '-0.02em', color: T.text }}>
          {title}
        </h2>
        {blurb ? (
          <p style={{ margin: '4px 0 0', fontSize: TYPE.meta, lineHeight: 1.5, color: T.textSecondary }}>
            {blurb}
          </p>
        ) : null}
      </div>
      {actions ? <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>{actions}</div> : null}
    </div>
  )
}
